import {
    animate,
    state,
    style,
    transition,
    trigger,
} from "@angular/animations";
import { DOCUMENT, AsyncPipe } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { Observable, Subscription } from "rxjs";

import { Context } from "@hermes/app-core";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

/**
 * This component allows you to manage the sticky block in mobile, this block mainly contains the add to cart
 */
@Component({
    selector: "h-sticky-wrapper",
    templateUrl: "./sticky-wrapper.component.html",
    styleUrls: ["./sticky-wrapper.component.scss"],
    animations: [
        trigger("stickyAnimation", [
            state(
                "visible",
                style({
                    transform: "translateY(0)",
                }),
            ),
            state(
                "hidden",
                style({
                    transform: "translateY(100%)",
                }),
            ),
            transition("visible <=> hidden", [animate("0.2s ease")]),
        ]),
    ],
    imports: [AsyncPipe],
    standalone: true,
})
export class StickyWrapperComponent implements OnInit, OnDestroy {
    /** True if sticky-wrapper block is hidden  */
    public hideStickyWrapper: false | boolean | undefined = false;

    /** True if we are in mobile mode */
    public isMobile$: Observable<boolean> = this.breakpointService.isMobile$;

    /** To save the subscription to be able to unsubscribe it in ngOnDestroy  */
    private subscription: Subscription = new Subscription();

    /**
     * Intersection observer to detect Drupal footer
     */
    public intersectionObserver: IntersectionObserver | undefined;

    constructor(
        private breakpointService: BreakpointService,
        private changeDetector: ChangeDetectorRef,
        private context: Context,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    public ngOnInit(): void {
        if (this.context.isInBrowserMode()) {
            this.changeDetector.detectChanges();
            this.subscription.add(
                this.isMobile$.subscribe((isMobile: boolean) => {
                    if (isMobile === false && this.intersectionObserver) {
                        this.intersectionObserver.disconnect();
                    } else {
                        this.registerIntersectionObserver();
                    }
                }),
            );
        }
    }

    /**
     * Hide sticky wrapper block on mobile when the footer is in viewport
     */
    public registerIntersectionObserver(): void {
        // Get footer element from Drupal
        const footerElement = this.document.getElementById("page-footer");

        // Get sub footer height
        let subFooterHeight = 0;
        const subFooterElement = this.document.getElementById("sub-footer");
        if (subFooterElement) {
            subFooterHeight = subFooterElement.offsetHeight;
        }

        // Add options to intersection observer
        // Value in rootMargin correspond to the sub footer height
        const options: IntersectionObserverInit = {
            rootMargin: `${subFooterHeight}px 0px`,
            threshold: 0,
        };

        this.intersectionObserver = new IntersectionObserver((entries) => {
            this.subscription.add(
                this.isMobile$.subscribe((isMobile) => {
                    entries.forEach((entry) => {
                        this.hideStickyWrapper =
                            entry.intersectionRatio > 0 && isMobile;
                    });
                }),
            );

            this.changeDetector.detectChanges();
        }, options);

        if (footerElement) {
            this.intersectionObserver.observe(footerElement);
        }
    }

    public ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }
}
